import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './adduser.css';

function AddUser() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [currentUser, setCurrentUser] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setCurrentUser(parsedUser);
      if (parsedUser.role === 'vendor') {
        setForm((prev) => ({ ...prev, role: 'user' }));
      }
    }
  }, []);

  useEffect(() => {
    if (form.password) {
      const strength = Math.min(
        Math.floor(
          (form.password.length / 12) * 100 +
            (/[A-Z]/.test(form.password) ? 20 : 0) +
            (/[0-9]/.test(form.password) ? 20 : 0) +
            (/[^A-Za-z0-9]/.test(form.password) ? 20 : 0)
        ),
        100
      );
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [form.password]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.match('image.*')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setSuccessMessage('User created successfully!');
      setForm({ name: '', email: '', password: '', role: 'user' });
      setAvatarPreview(null);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to create user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return '#ff4d4f';
    if (passwordStrength < 70) return '#faad14';
    return '#52c41a';
  };

  return (
    <div className="add-user-container">
      <div className="add-user-card">
        <div className="add-user-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor" />
              <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor" />
            </svg>
          </div>
          <h2>Add New User</h2>
          <p>Add a new team member to your organization</p>
        </div>

        {successMessage && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            <span>{successMessage}</span>
            <button className="alert-close" onClick={() => setSuccessMessage('')}>×</button>
          </div>
        )}
        
        {errorMessage && (
          <div className="alert alert-error">
            <span className="alert-icon">!</span>
            <span>{errorMessage}</span>
            <button className="alert-close" onClick={() => setErrorMessage('')}>×</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          

          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              placeholder="Enter full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <div className="password-strength">
              <div 
                className="strength-bar" 
                style={{ 
                  width: `${passwordStrength}%`,
                  backgroundColor: getPasswordStrengthColor()
                }} 
              />
            </div>
            <div className="password-hints">
              <span className={form.password.length >= 8 ? 'valid' : ''}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  {form.password.length >= 8 ? (
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                </svg>
                8+ characters
              </span>
              <span className={/[A-Z]/.test(form.password) ? 'valid' : ''}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  {/[A-Z]/.test(form.password) ? (
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                </svg>
                Uppercase
              </span>
              <span className={/[0-9]/.test(form.password) ? 'valid' : ''}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  {/[0-9]/.test(form.password) ? (
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                </svg>
                Number
              </span>
              <span className={/[^A-Za-z0-9]/.test(form.password) ? 'valid' : ''}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  {/[^A-Za-z0-9]/.test(form.password) ? (
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                </svg>
                Special
              </span>
            </div>
          </div>

          {currentUser?.role === 'admin' ? (
            <div className="form-group">
              <label>Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="vendor">Vendor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          ) : (
            <input type="hidden" name="role" value="user" />
          )}

          {currentUser?.role === 'vendor' && (
            <div className="info-note">Vendor can only create standard users.</div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="spinner" viewBox="0 0 50 50">
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                </svg>
                Creating Account...
              </>
            ) : 'Create Account'}
          </button>
        </form>


      </div>
    </div>
  );
}

export default AddUser;